import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { TYPE_LABELS } from "@/types/movie";
import { formatViews } from "@/lib/catalog";

interface MovieCardProps {
  movie: Movie;
  showSummary?: boolean;
}

export default function MovieCard({ movie, showSummary = false }: MovieCardProps) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 border border-background-300/50 hover:border-primary-500/60 transition-all duration-300"
    >
      <div className="relative w-full aspect-[5/7] overflow-hidden bg-background-200">
        <img
          src={movie.poster}
          alt={`${movie.title} 海报`}
          title={`${movie.title} 海报`}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent opacity-80" />

        <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-accent-500 text-background-950 text-xs font-heading font-bold">
          {movie.rating.toFixed(1)}
        </span>
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-background-50/80 backdrop-blur text-foreground-800 text-[11px] font-medium">
          {TYPE_LABELS[movie.type]}
        </span>

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="w-14 h-14 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-play-fill text-2xl" />
          </span>
        </div>

        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-[11px] text-foreground-700">
          <span className="px-1.5 py-0.5 rounded bg-background-200/80 backdrop-blur">
            {movie.year}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-background-200/80 backdrop-blur">
            {movie.region}
          </span>
          <span className="ml-auto flex items-center gap-1 text-foreground-600">
            <i className="ri-fire-line text-primary-500" />
            {formatViews(movie.views)}
          </span>
        </div>
      </div>

      <div className="p-3">
        <h3 className="font-heading text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-600 truncate">
          {movie.genres.join(" · ")}
          {showSummary ? "" : ` · ${movie.duration}`}
        </p>
        {showSummary && (
          <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
            {movie.summary}
          </p>
        )}
      </div>
    </Link>
  );
}